import { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";

import {
  DATE_PRESETS,
  formatIsoDate,
  matchPreset,
  parseIsoDate,
  presetRange,
  type DatePresetKey,
} from "../dateRange";
import { isoDate } from "../format";

interface Props {
  /** id for the trigger so a <label htmlFor> can point at it. */
  id?: string;
  /** ISO `yyyy-MM-dd`, empty string = unset. */
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  ariaLabel?: string;
  /** Show the × that empties the range. Off for reports, whose queries need both ends. */
  allowClear?: boolean;
  className?: string;
}

const WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
/**
 * One 17rem month + padding, in px — the narrowest the open popover can be. It stretches to the
 * trigger's width when that is wider, so the calendar lines up with the field above it.
 */
const POPOVER_WIDTH = 272 + 16;

function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** 42 cells (6 weeks) starting from the Monday on or before the 1st. */
function monthGrid(month: Date): Date[] {
  const first = firstOfMonth(month);
  const lead = (first.getDay() + 6) % 7; // Monday = 0
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead);
  return Array.from(
    { length: 42 },
    (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  );
}

/**
 * Date range picker in the SalePro admin's RangePicker layout: one input-shaped trigger showing
 * `dd/MM/yyyy → dd/MM/yyyy`, which opens a single-month calendar (« ‹ month › » in its header) with a
 * footer of quick presets (Hôm nay … Năm nay — the active one filled). One month, not two: the
 * presets already cover the week/month jumps, and a second month only made the popover wide. Click a start day, then an end day; picking a preset or
 * finishing a range closes the popover. No date library: plain local-time `Date` + ISO strings.
 */
export function DateRangePicker({
  id,
  from,
  to,
  onChange,
  ariaLabel = "Khoảng ngày",
  allowClear = true,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const today = useMemo(() => isoDate(new Date()), []);

  // Visible month: the start of the current range, else today's month.
  const [view, setView] = useState<Date>(() =>
    firstOfMonth(parseIsoDate(from) ?? new Date()),
  );
  // A click on a day while a range is "half-picked" (start chosen, end not yet).
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  // Anchor the popover to the trigger's right edge when it would otherwise run off-screen (the
  // range field usually sits in the last filter column).
  const [alignRight, setAlignRight] = useState(false);

  const activePreset = matchPreset(from, to);

  useEffect(() => {
    if (!open) return;
    setView(firstOfMonth(parseIsoDate(from) ?? new Date()));
    setPendingStart(null);
    setHover(null);
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect)
      setAlignRight(
        rect.left + Math.max(POPOVER_WIDTH, rect.width) > window.innerWidth - 16,
      );
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // `from` is only read when opening — a change while open is ours and must not reset the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function pickDay(iso: string) {
    if (!pendingStart) {
      setPendingStart(iso);
      return;
    }
    const [a, b] =
      pendingStart <= iso ? [pendingStart, iso] : [iso, pendingStart];
    setPendingStart(null);
    onChange({ from: a, to: b });
    setOpen(false);
  }

  function pickPreset(key: DatePresetKey) {
    onChange(presetRange(key));
    setPendingStart(null);
    setOpen(false);
  }

  // The range to shade: the committed one, or the half-picked one stretched to the hovered day.
  const shade = useMemo<{ from: string; to: string } | null>(() => {
    if (pendingStart) {
      const end = hover ?? pendingStart;
      return pendingStart <= end
        ? { from: pendingStart, to: end }
        : { from: end, to: pendingStart };
    }
    return from && to ? { from, to } : null;
  }, [pendingStart, hover, from, to]);

  const hasValue = Boolean(from || to);
  const showClear = allowClear && hasValue;

  function renderMonth(month: Date) {
    const cells = monthGrid(month);
    const m = month.getMonth();
    return (
      <div className="w-full px-2">
        <div className="flex h-9 items-center justify-between text-sm font-semibold">
          <div className="flex items-center">
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setView((v) => addMonths(v, -12))}
              aria-label="Năm trước"
            >
              <ChevronsLeft className="size-4" />
            </button>
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setView((v) => addMonths(v, -1))}
              aria-label="Tháng trước"
            >
              <ChevronLeft className="size-4" />
            </button>
          </div>
          <span>
            Th {m + 1} {month.getFullYear()}
          </span>
          <div className="flex items-center">
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setView((v) => addMonths(v, 1))}
              aria-label="Tháng sau"
            >
              <ChevronRight className="size-4" />
            </button>
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setView((v) => addMonths(v, 12))}
              aria-label="Năm sau"
            >
              <ChevronsRight className="size-4" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 text-center text-xs text-muted-foreground">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7" role="grid">
          {cells.map((d) => {
            const iso = isoDate(d);
            const outside = d.getMonth() !== m;
            const isStart = shade?.from === iso;
            const isEnd = shade?.to === iso;
            const inRange = shade ? iso > shade.from && iso < shade.to : false;
            return (
              <button
                key={iso}
                type="button"
                role="gridcell"
                aria-label={formatIsoDate(iso)}
                aria-pressed={isStart || isEnd}
                onClick={() => pickDay(iso)}
                onMouseEnter={() => setHover(iso)}
                onMouseLeave={() => setHover(null)}
                className={cn(
                  "relative mx-auto my-0.5 flex h-8 w-8 items-center justify-center rounded-md text-sm tabular-nums transition-colors",
                  outside && "text-muted-foreground/50",
                  !outside && !isStart && !isEnd && "hover:bg-accent",
                  inRange && "rounded-none bg-primary/10",
                  (isStart || isEnd) && "bg-primary text-primary-foreground",
                  iso === today &&
                    !isStart &&
                    !isEnd &&
                    "ring-1 ring-inset ring-primary",
                )}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        id={id}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          showClear && "pr-9",
        )}
      >
        <span
          className={cn(
            "flex-1 text-left tabular-nums",
            !from && "text-muted-foreground",
          )}
        >
          {from ? formatIsoDate(from) : "Từ ngày"}
        </span>
        <span className="text-muted-foreground" aria-hidden="true">
          →
        </span>
        <span
          className={cn(
            "flex-1 text-left tabular-nums",
            !to && "text-muted-foreground",
          )}
        >
          {to ? formatIsoDate(to) : "Đến ngày"}
        </span>
        {!showClear && (
          <CalendarIcon
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </button>
      {showClear && (
        // A sibling, not a child: interactive content inside a <button> is invalid and reads badly
        // to assistive tech (same placement as the search box's clear button in Toolbar).
        <button
          type="button"
          aria-label="Xoá khoảng ngày"
          onClick={() => {
            onChange({ from: "", to: "" });
            setOpen(false);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" />
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Chọn khoảng ngày"
          className={cn(
            // As wide as the trigger (min-w 100%), never narrower than one month (18rem). Only one
            // horizontal inset is set so the box can grow past a trigger narrower than that.
            "absolute z-50 mt-1 min-w-[max(18rem,100%)] max-w-[calc(100vw-2rem)] rounded-md border bg-popover p-2 text-popover-foreground shadow-md",
            alignRight ? "right-0" : "left-0",
          )}
        >
          {renderMonth(view)}
          {pendingStart && (
            <p className="px-2 pt-2 text-xs text-muted-foreground">
              Bắt đầu {formatIsoDate(pendingStart)} — chọn ngày kết thúc.
            </p>
          )}
          <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border px-2 pt-2">
            {DATE_PRESETS.map((p, i) => {
              const active = activePreset === p.key && !pendingStart;
              const lastAlone =
                i === DATE_PRESETS.length - 1 && DATE_PRESETS.length % 2 !== 0;
              return (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => pickPreset(p.key)}
                  className={cn(
                    "h-9 rounded-md border border-primary px-2 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground",
                    active && "bg-primary text-primary-foreground",
                    lastAlone && "col-span-2",
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
